// src/components/StarlinkUsageOverview.jsx
//
// Admin-only. The same "ours vs sold vs used" quota bar as the admin
// Starlink device page (QuotaPanel), but one row per SERVICE LINE, so the
// team can see the whole fleet's top-up situation at once.
//
// Why service line, not kit: the subscription and the usage counter
// belong to the service line. Kits sharing one (spares, swapped dishes)
// report identical numbers, so one row lists all of them together
// instead of repeating the same bar. Each kit is clickable to its own
// device page.
//
// Data: GET /api/v2/admin/starlink-overview - built from the usage the
// server syncs itself every 15 min (sites/starlink_sync.py, one worker,
// no live satellite call per page load), and
// the same compute_quota() the device page uses, so a row's status and
// numbers always match that terminal's own page.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetchJSON } from '../api/apiClient';
import FleetHeader from './FleetHeader';
import { fmt1, fmt2 } from './monitoring/t247Shared';
import '../styles/fleet-theme.css';
import './StarlinkDevicePage.css';
import './StarlinkUsageOverview.css';

const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';
const POLL_MS = 5 * 60 * 1000;
const SYNC_POLL_MS = 5000;

// Same labels/tiers as QUOTA_LEVELS in StarlinkDevicePage.jsx, plus
// no_usage (terminal known, but no usage snapshot synced yet).
const LEVELS = {
  exhausted:    { tier: 'critical', label: 'SUBSCRIPTION USED UP' },
  topup_needed: { tier: 'critical', label: 'TOP UP NEEDED' },
  watch:        { tier: 'warning',  label: 'FORECAST OVER SUB' },
  ok:           { tier: 'good',     label: 'WITHIN SUBSCRIPTION' },
  covered:      { tier: 'good',     label: 'FULLY COVERED' },
  no_plan:      { tier: 'warning',  label: 'NO PAID PLAN' },
  unlinked:     { tier: 'warning',  label: 'BILLING NOT LINKED' },
  unavailable:  { tier: 'warning',  label: 'BILLING LOOKUP FAILED' },
  unknown:      { tier: 'warning',  label: 'NO SUBSCRIPTION DATA' },
  no_usage:     { tier: 'muted',    label: 'AWAITING SYNC' },
  // Every kit on the line dropped out of ELCOME (see quota.fleet_overview).
  missing:      { tier: 'muted',    label: 'MISSING FROM ELCOME' },
};

// A kit is "missing" when ELCOME stopped returning it in the terminal
// list (no service line any more, or removed from our account). The
// server flags it (missing_since) instead of deleting it; an admin
// deletes it here once they've confirmed it's really gone.
const isMissingKit = (k) => !!k?.missing_since;
const missingKits = (t) => (t.kits || []).filter(isMissingKit);
const isMissingRow = (t) =>
  t.missing ?? ((t.kits || []).length > 0 && (t.kits || []).every(isMissingKit));

const FILTERS = [
  { id: 'all',    label: 'All',              levels: null },
  { id: 'action', label: 'Top up now',       levels: ['exhausted', 'topup_needed'] },
  { id: 'watch',  label: 'Forecast over',    levels: ['watch'] },
  { id: 'setup',  label: 'Billing / setup',  levels: ['no_plan', 'unlinked', 'unavailable', 'unknown', 'no_usage'] },
  { id: 'fine',   label: 'Fine',             levels: ['ok', 'covered'] },
  // Not a quota level - any row with at least one missing kit.
  { id: 'missing', label: 'Missing',         test: (t) => missingKits(t).length > 0 },
];

const SORTS = [
  { id: 'urgency',  label: 'Most urgent' },
  { id: 'percent',  label: '% of our subscription' },
  { id: 'shortfall', label: 'Suggested top-up' },
  { id: 'name',     label: 'Site name' },
];

function fmtGB(gb) {
  if (gb == null) return '—';
  const n = Number(gb);
  if (n >= 1000) return `${+(n / 1000).toFixed(2)} TB`;
  return `${+n.toFixed(2)} GB`;
}

function formatShortDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}

function timeAgo(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} h ago`;
  return `${Math.round(hrs / 24)} days ago`;
}

export default function StarlinkUsageOverview({ user, onLogout }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [sync, setSync] = useState(null); // /admin/starlink/sync-status
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('urgency');
  const [deleting, setDeleting] = useState(null); // device_key being deleted
  const [actionError, setActionError] = useState(null); // failed delete - shown as a banner, page stays

  const load = useCallback(async ({ silent = false, refreshQuota = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const json = await apiFetchJSON(
        `/api/v2/admin/starlink-overview${refreshQuota ? '?refresh_quota=true' : ''}`
      );
      setData(json);
      setError(null);
    } catch (e) {
      // A failed background poll keeps the numbers already on screen.
      if (!silent) setError(e.message || 'Could not load Starlink usage');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadSync = useCallback(async () => {
    try {
      setSync(await apiFetchJSON('/api/v2/admin/starlink/sync-status'));
    } catch {
      // Status is informational; the page works without it.
    }
  }, []);

  useEffect(() => { loadSync(); }, [loadSync]);

  // While a sync runs (started here, by the timer, or by another admin),
  // poll its status; when it finishes, reload the numbers.
  useEffect(() => {
    if (!sync?.running) return undefined;
    const t = setInterval(async () => {
      try {
        const next = await apiFetchJSON('/api/v2/admin/starlink/sync-status');
        setSync(next);
        if (!next.running) load({ silent: true });
      } catch {
        // try again next tick
      }
    }, SYNC_POLL_MS);
    return () => clearInterval(t);
  }, [sync?.running, load]);

  const syncNow = async () => {
    try {
      const res = await apiFetchJSON('/api/v2/admin/starlink/sync-now', { method: 'POST' });
      setSync({ ...res, running: true });
    } catch (e) {
      setSync((prev) => ({ ...(prev || {}), lastError: e.message || 'Could not start the sync' }));
    }
  };

  useEffect(() => {
    const t = setInterval(() => { load({ silent: true }); loadSync(); }, POLL_MS);
    return () => clearInterval(t);
  }, [load, loadSync]);

  const deleteMissing = async (kit) => {
    const name = kit.kit_serial || kit.device_key;
    const ok = window.confirm(
      `Delete ${name} permanently?\n\n` +
      `ELCOME no longer returns this kit${kit.missing_since ? ` (missing since ${formatShortDate(kit.missing_since.replace(' ', 'T') + 'Z')})` : ''}. ` +
      'This removes its service line from Starlink Usage. The kit stays on its site, shown as ROAM; ' +
      'unassign it in Site Manager once it is physically gone. ' +
      'If it gets a service line on ELCOME again, it comes back here automatically.'
    );
    if (!ok) return;
    setDeleting(kit.device_key);
    setActionError(null);
    try {
      await apiFetchJSON(`/api/v2/admin/starlink/${encodeURIComponent(kit.device_key)}/missing`, { method: 'DELETE' });
      await load({ silent: true });
    } catch (e) {
      setActionError(e.message || 'Could not delete this kit');
    } finally {
      setDeleting(null);
    }
  };

  const recheck = async () => {
    setRechecking(true);
    await load({ silent: true, refreshQuota: true });
    setRechecking(false);
  };

  const terminals = data?.terminals || [];
  const levels = data?.summary?.levels || {};

  const filterCount = (f) =>
    f.test
      ? terminals.filter(f.test).length
      : f.levels ? f.levels.reduce((n, l) => n + (levels[l] || 0), 0) : terminals.length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = FILTERS.find((f) => f.id === filter);
    let rows = terminals.filter((t) => {
      if (active?.test && !active.test(t)) return false;
      if (active?.levels && !active.levels.includes(t.level)) return false;
      if (!q) return true;
      const kitFields = (t.kits || []).flatMap((k) => [k.kit_serial, k.device_key, k.label, k.site_name]);
      return [t.site_name, t.client_name, t.yacht_name, t.service_line, ...kitFields]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
    if (sort !== 'urgency') {
      // Server order is already "most urgent"; other sorts re-order a copy.
      rows = [...rows].sort((a, b) => {
        if (sort === 'name') {
          return (a.site_name || a.name || '').localeCompare(b.site_name || b.name || '');
        }
        const key = sort === 'percent' ? 'percentOfSubscribed' : 'projectedShortfallGB';
        return ((b.quota || {})[key] || 0) - ((a.quota || {})[key] || 0);
      });
    }
    return rows;
  }, [terminals, query, filter, sort]);

  if (loading) {
    return (
      <div className="cm-loading-overlay">
        <div className="cm-spinner-container">
          <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
            <source src={loadingVideo} type="video/mp4" />
          </video>
          <p>Loading Starlink usage…</p>
        </div>
      </div>
    );
  }

  const s = data?.summary || {};
  const needAction = (levels.exhausted || 0) + (levels.topup_needed || 0);
  const setupIssues = filterCount(FILTERS.find((f) => f.id === 'setup'));
  const usedPctOfSub = s.subscribedGB ? (s.usedGB / s.subscribedGB) * 100 : null;
  const threshold = data?.topupThresholdPercent ?? 80;

  return (
    <div>
      <FleetHeader
        title="Starlink Usage"
        user={user}
        onLogout={onLogout}
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="search site, client, kit serial"
      />
      <div className="asd-page sov-page">

        <div className="asd-identity-bar">
          <div className="asd-crumb">
            <span>STARLINK</span>
            <span>/</span>
            <span className="current">USAGE OVERVIEW</span>
          </div>
          <div className="asd-topbar-right">
            <span className="asd-view-chip">ADMIN VIEW</span>
            <span className="sov-sync-note">
              {sync?.running
                ? 'Syncing usage from Starlink…'
                : sync?.finishedAt
                  ? `Usage synced ${timeAgo(sync.finishedAt)} · auto every ${sync.intervalMinutes} min`
                  : data?.newestSyncAt
                    ? `Usage synced ${timeAgo(data.newestSyncAt)}`
                    : 'Usage not synced yet'}
            </span>
            <button className="sov-btn" onClick={syncNow} disabled={!!sync?.running || sync?.configured === false}>
              {sync?.running ? 'Syncing…' : 'Sync now'}
            </button>
            <button className="sov-btn" onClick={recheck} disabled={rechecking}>
              {rechecking ? 'Checking invoices…' : 'Re-check invoices'}
            </button>
          </div>
        </div>

        {sync?.configured === false && (
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            The satellite API isn't configured on this server, so usage can't be synced. Set the API credentials and restart.
          </div>
        )}
        {sync?.configured !== false && sync?.lastError && !sync?.running && (
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Last sync failed: {sync.lastError}. Numbers below are from the last successful sync.
          </div>
        )}
        {!sync?.running && sync?.failed > 0 && !sync?.lastError && (
          <div className="sov-sync-problem soft">
            {sync.failed} of {sync.synced + sync.failed} terminals couldn't be read in the last sync; their rows keep the previous reading.
          </div>
        )}

        {actionError && (
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {actionError}
            <button type="button" className="btn-close" onClick={() => setActionError(null)}></button>
          </div>
        )}

        {error && (
          <div className="ft-toolbar-error" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {!error && (
          <>
            {/* ── FLEET SUMMARY ─────────────────────────────────────────── */}
            <div className="asd-hero sov-hero">
              <div className="t247-eyebrow">
                FLEET · THIS CYCLE · {s.count || 0} SERVICE {s.count === 1 ? 'LINE' : 'LINES'} · {s.kitCount || 0} KITS
                {s.missingKitCount > 0 && ` · ${s.missingKitCount} MISSING`}
              </div>
              <div className="asd-hero-number">
                <span className="big">{needAction}</span>
                <span className="unit">
                  {needAction === 1 ? 'service line needs' : 'service lines need'} a top-up now
                </span>
                {needAction > 0 && (
                  <button
                    type="button"
                    className="asd-usage-status critical sov-hero-link"
                    onClick={() => setFilter('action')}
                  >
                    SHOW THEM
                  </button>
                )}
              </div>

              <div className="t247-statbox-grid sov-stats">
                <StatBox label="FORECAST OVER SUBSCRIPTION" value={levels.watch || 0} unit="lines" />
                <StatBox label="BILLING / SETUP ISSUES" value={setupIssues} unit="lines" />
                <StatBox
                  label="USED OF OUR SUBSCRIPTIONS"
                  value={usedPctOfSub != null ? fmt1(usedPctOfSub) : '—'}
                  unit={usedPctOfSub != null ? `% · ${fmtGB(s.usedGB)} of ${fmtGB(s.subscribedGB)}` : ''}
                />
                <StatBox label="SOLD TO CLIENTS" value={fmtGB(s.soldGB)} small />
                <StatBox label="SUGGESTED TOP-UPS" value={fmt2(s.suggestedTopupGB || 0)} unit="GB" />
              </div>
            </div>

            {/* ── CONTROLS + LEGEND ─────────────────────────────────────── */}
            <div className="sov-controls">
              <div className="sov-filters" role="tablist" aria-label="Filter terminals">
                {FILTERS.filter((f) => f.id !== 'missing' || filterCount(f) > 0 || filter === 'missing').map((f) => (
                  <button
                    key={f.id}
                    role="tab"
                    aria-selected={filter === f.id}
                    className={`sov-filter ${filter === f.id ? 'active' : ''}`}
                    onClick={() => setFilter(f.id)}
                  >
                    {f.label} <span className="sov-filter-count">{filterCount(f)}</span>
                  </button>
                ))}
              </div>
              <label className="sov-sort">
                <span>SORT</span>
                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  {SORTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </label>
            </div>

            <div className="sov-legend" aria-hidden="true">
              <span><i className="sov-key fill" /> Used</span>
              <span><i className="sov-key forecast" /> Forecast to cycle end</span>
              <span><i className="sov-key ours" /> Our subscription</span>
              <span><i className="sov-key threshold" /> Top-up threshold ({threshold}%)</span>
              <span><i className="sov-key client" /> Client plan</span>
              <span className="sov-legend-note">One row per service line · each bar is scaled to its own line.</span>
            </div>

            {/* ── TERMINAL ROWS ─────────────────────────────────────────── */}
            <div className="sov-list">
              {visible.length === 0 ? (
                <div className="t247-empty">
                  {terminals.length === 0
                    ? 'No managed Starlink service lines yet. Press Sync now to load the terminal list.'
                    : 'No terminals match this filter or search.'}
                </div>
              ) : (
                visible.map((t) => (
                  <UsageRow
                    key={t.service_line || t.device_key}
                    t={t}
                    onOpen={(deviceKey) => navigate(`/admin/starlink/${deviceKey || t.device_key}`)}
                    onDeleteMissing={deleteMissing}
                    deleting={deleting}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── ONE SERVICE LINE ─────────────────────────────────────────────────
// Compact version of QuotaPanel's bar: same marks, same colours. The
// title carries every kit on the line; clicking a kit opens that kit,
// clicking elsewhere on the row opens the kit whose usage is shown.
function UsageRow({ t, onOpen, onDeleteMissing, deleting }) {
  const rowMissing = isMissingRow(t);
  // A fully missing line keeps its last reading, but the status that
  // matters is that ELCOME dropped it - the server sets level 'missing'.
  const level = rowMissing ? LEVELS.missing : (LEVELS[t.level] || LEVELS.unknown);
  const q = t.quota;

  // The detail page needs the terminal to exist in ELCOME, so a fully
  // missing row has nowhere to go.
  const open = (deviceKey) => { if (!rowMissing) onOpen(deviceKey); };
  const onKey = (e) => {
    if (e.target !== e.currentTarget) return; // a kit link handles its own keys
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
  };
  const kits = t.kits || [];

  return (
    <div
      className={`sov-row tier-${level.tier}${rowMissing ? ' sov-row-missing' : ''}`}
      role={rowMissing ? undefined : 'link'}
      tabIndex={rowMissing ? undefined : 0}
      onClick={() => open()}
      onKeyDown={onKey}
    >
      <div className="sov-ident">
        <div className="sov-site-name">{t.site_name || 'Unassigned'}</div>
        <div className="sov-kits">
          {kits.map((k, i) => {
            const missing = isMissingKit(k);
            return (
              <React.Fragment key={k.device_key}>
                {i > 0 && <span className="sov-kit-sep">+</span>}
                <button
                  type="button"
                  className={`sov-kit${missing ? ' missing' : ''}`}
                  title={
                    missing
                      ? `Missing from ELCOME since ${k.missing_since} · UTID ${k.device_key}`
                      : `${k.online ? 'Online' : 'Offline'} · UTID ${k.device_key}${k.site_name && k.site_name !== t.site_name ? ` · on ${k.site_name}` : ''}`
                  }
                  disabled={missing}
                  onClick={(e) => { e.stopPropagation(); if (!missing) onOpen(k.device_key); }}
                >
                  <span className={`sov-online ${k.online && !missing ? 'on' : ''}`} />
                  {k.kit_serial || k.device_key}
                  {missing && <span className="sov-missing-tag">MISSING</span>}
                </button>
                {missing && (
                  <button
                    type="button"
                    className="sov-btn sov-btn-delete"
                    disabled={deleting === k.device_key}
                    onClick={(e) => { e.stopPropagation(); onDeleteMissing(k); }}
                  >
                    {deleting === k.device_key ? 'Deleting…' : 'DELETE'}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="sov-sub">
          {t.service_line || 'No service line'}{t.client_name ? ` · ${t.client_name}` : ''}
        </div>
      </div>

      <div className="sov-barcol">
        {q ? <QuotaBar q={q} /> : (
          <div className="sov-nobar">No usage reading yet. It appears after the next sync, or press Sync now.</div>
        )}
      </div>

      <div className="sov-statuscol">
        <span className={`asd-usage-status ${level.tier === 'muted' ? '' : level.tier} sov-chip`}>{level.label}</span>
        <RowFootnote t={t} />
      </div>
    </div>
  );
}

function QuotaBar({ q }) {
  const sub = q.subscribedGB || 0;
  const client = q.clientGB || 0;
  const used = q.usedGB || 0;
  const projected = q.projectedGB || used;
  const scale = Math.max(sub, client, used, projected, 1);
  const pct = (gb) => Math.min(100, (gb / scale) * 100);
  const pos = (gb) => `${pct(gb)}%`;
  const labelAt = (gb) => {
    const p = pct(gb);
    return { left: `${p}%`, transform: `translateX(${p < 12 ? '0' : p > 88 ? '-100%' : '-50%'})` };
  };
  // Bar colour shows usage risk only; billing/setup problems are the chip's job.
  const tier = { exhausted: 'critical', topup_needed: 'critical', watch: 'warning' }[q.level] || '';

  return (
    <div
      className="sov-bar"
      role="img"
      aria-label={`${fmt2(used)} GB used of ${fmtGB(sub)} subscribed${client ? `, ${fmtGB(client)} sold to the client` : ''}, forecast ${fmt2(projected)} GB`}
    >
      <div className="sov-figures">
        <strong>{fmt2(used)} GB</strong>
        <span>
          {q.percentOfSubscribed != null ? `${fmt1(q.percentOfSubscribed)}% of ours` : 'used'}
          {projected > used ? ` · forecast ${fmtGB(projected)}` : ''}
        </span>
      </div>
      <div className="sov-track">
        {projected > used && (
          <div className="sov-forecast" style={{ left: pos(used), width: `calc(${pos(projected)} - ${pos(used)})` }} />
        )}
        <div className={`sov-fill ${tier}`} style={{ width: pos(used) }} />
        {sub > 0 && <div className="sov-mark threshold" style={{ left: pos(q.topupThresholdGB) }} />}
        {sub > 0 && <div className="sov-mark ours" style={{ left: pos(sub) }} />}
        {client > 0 && <div className="sov-mark client" style={{ left: pos(client) }} />}
      </div>
      <div className="sov-scale">
        {sub > 0 && <span className="ours" style={labelAt(sub)}>Ours {fmtGB(sub)}</span>}
        {client > 0 && Math.abs(client - sub) / scale > 0.18 && (
          <span className="client" style={labelAt(client)}>Client {fmtGB(client)}</span>
        )}
      </div>
    </div>
  );
}

// The one extra fact worth reading per status, beside the chip.
function RowFootnote({ t }) {
  const q = t.quota || {};
  const lines = [];

  const gone = missingKits(t);
  if (gone.length) {
    const since = gone.map((k) => k.missing_since).filter(Boolean).sort()[0];
    lines.push(
      isMissingRow(t)
        ? `Not returned by ELCOME${since ? ` since ${formatShortDate(since.replace(' ', 'T') + 'Z')}` : ''} · reading below is the last one`
        : `${gone.length} kit${gone.length > 1 ? 's' : ''} no longer on this line in ELCOME`
    );
    if (isMissingRow(t)) {
      return <div className="sov-foot">{lines.map((l) => <div key={l}>{l}</div>)}</div>;
    }
  }

  if (['exhausted', 'topup_needed', 'watch'].includes(t.level)) {
    if (q.projectedShortfallGB) lines.push(`Top up ${fmtGB(q.projectedShortfallGB)}`);
    if (t.level === 'exhausted') lines.push('Client likely throttled');
    else if (q.subscriptionRunsOutOn) lines.push(`Runs out ${formatShortDate(q.subscriptionRunsOutOn)}`);
  } else if (t.level === 'unlinked') {
    lines.push(t.site_name ? 'Link a yacht in Site Manager' : 'Assign to a site in Site Manager');
  } else if (t.level === 'no_plan') {
    lines.push('No paid invoice this month');
  } else if (['ok', 'covered'].includes(t.level) && q.cycleEndDate) {
    lines.push(`Resets ${formatShortDate(q.cycleEndDate)}`);
  }

  if (t.billing_mismatch) lines.push('Kits on sites linked to different yachts');
  else if (t.site_mismatch) lines.push(`Kits on ${t.site_names.length} sites: ${t.site_names.join(', ')}`);
  if (q.lastAlert) lines.push(`Team alerted ${formatShortDate(q.lastAlert.created_at?.replace(' ', 'T') + 'Z')}`);
  if (t.cycle_ended) lines.push('Reading is from last cycle');
  else if (t.stale && t.usage_synced_at) lines.push(`Synced ${timeAgo(t.usage_synced_at)}`);

  if (!lines.length) return null;
  return (
    <div className="sov-foot">
      {lines.map((l) => <div key={l} className={l.startsWith('Synced') || l.startsWith('Reading') ? 'stale' : ''}>{l}</div>)}
    </div>
  );
}

const StatBox = ({ label, value, unit, small }) => (
  <div className="t247-statbox">
    <div className="label">{label}</div>
    <div className={`value ${small ? 'small' : ''}`}>
      {value} {unit && <span className="unit">{unit}</span>}
    </div>
  </div>
);
